import { useSessionStore } from "@/plugins/store/session";
import { useCheckoutStore } from "@/plugins/store/checkout";
import { usePaymentsStore } from "@/plugins/store/payments";
import { useTruelayerStore } from "@/plugins/store/providers/truelayer";
import { useVoltStore } from "@/plugins/store/providers/volt";
import { useLloydsStore } from "@/plugins/store/providers/lloyds";
import { useIFXStore } from "@/plugins/store/providers/ifx";
import { useEMandatesStore } from "@/plugins/store/providers/emandates";
import { generateUUID } from "@/helpers/uuid";

/**
 * Composable that provides the three cross-domain clear methods.
 * Use this directly in components/composables that have migrated away from
 * the monolithic store, or call it from the facade store.
 *
 * clearSessionData                  — full session reset (logout / end of session)
 * clearSessionDataForAnotherPayment — partial reset when starting a second payment
 * clearSessionDataForRetryPayment   — partial reset when retrying a failed payment
 */
export function useSessionClear() {
    const sessionStore = useSessionStore();
    const checkoutStore = useCheckoutStore();
    const paymentsStore = usePaymentsStore();
    const truelayerStore = useTruelayerStore();
    const voltStore = useVoltStore();
    const lloydsStore = useLloydsStore();
    const ifxStore = useIFXStore();
    const eMandatesStore = useEMandatesStore();

    function clearSessionData() {
        /**
         * Resets data to default values on session end
         */
        sessionStore.setSessionId(generateUUID());
        sessionStore.setUser(null);
        sessionStore.setEmailAddress(null);
        checkoutStore.setSupplementaryData(null);
        checkoutStore.setQueryStringData(null);
        checkoutStore.setPayerDetails(null);
        paymentsStore.setPaymentId(null);
        paymentsStore.setPaymentMethods(null);
        paymentsStore.setSelectedProvider(null);
        paymentsStore.setSelectedProviderDetails(null);
        checkoutStore.setTriggerPayerInformationValidation(false);
        checkoutStore.setTriggerCardInformationValidation(false);
        checkoutStore.setPartnerLinkInformation(null);
        truelayerStore.setTruelayerPaymentInformation(null);
        voltStore.setVoltPaymentInformation(null);
        eMandatesStore.setAccountAlias(null);
        eMandatesStore.setValidatedAccountInformation(null);
        eMandatesStore.setEMandatesInformation(null);
        eMandatesStore.setEMandatesInstruction(null);
        checkoutStore.setOrderId(null);
        lloydsStore.setLloyds3DSMethodData(null);
        lloydsStore.setLloydsChallengeData(null);
        lloydsStore.setLloyds3DSMethodDataConsumed(false);
        lloydsStore.setLloydsChallengeDataConsumed(false);
        checkoutStore.setOverrideInvalidQueryStringData([]);
        ifxStore.setIFXPaymentInformation(null);
        ifxStore.setIFXBankTransferInformation(null);
    }

    function clearSessionDataForAnotherPayment() {
        /** Resets certain values when the user starts a second payment */
        checkoutStore.setSupplementaryData(null);
        checkoutStore.setQueryStringData(null);
        paymentsStore.setPaymentId(null);
        paymentsStore.setPaymentMethods(null);
        checkoutStore.setTriggerPayerInformationValidation(false);
        checkoutStore.setTriggerCardInformationValidation(false);
        truelayerStore.setTruelayerPaymentInformation(null);
        voltStore.setVoltPaymentInformation(null);
        lloydsStore.setLloyds3DSMethodData(null);
        lloydsStore.setLloydsChallengeData(null);
        lloydsStore.setLloyds3DSMethodDataConsumed(false);
        lloydsStore.setLloydsChallengeDataConsumed(false);
        checkoutStore.setOverrideInvalidQueryStringData([]);
        ifxStore.setIFXPaymentInformation(null);
        ifxStore.setIFXBankTransferInformation(null);
    }

    function clearSessionDataForRetryPayment() {
        /** Resets certain values when the user starts a second payment */
        paymentsStore.setPaymentId(null);
        paymentsStore.setPaymentMethods(null);
        checkoutStore.setTriggerPayerInformationValidation(false);
        checkoutStore.setTriggerCardInformationValidation(false);
        truelayerStore.setTruelayerPaymentInformation(null);
        voltStore.setVoltPaymentInformation(null);
        lloydsStore.setLloyds3DSMethodData(null);
        lloydsStore.setLloydsChallengeData(null);
        lloydsStore.setLloyds3DSMethodDataConsumed(false);
        lloydsStore.setLloydsChallengeDataConsumed(false);
        ifxStore.setIFXPaymentInformation(null);
        ifxStore.setIFXBankTransferInformation(null);
    }

    return {
        clearSessionData,
        clearSessionDataForAnotherPayment,
        clearSessionDataForRetryPayment,
    };
}
